import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiTruck, FiShield, FiHeadphones, FiRefreshCw, FiArrowRight, FiStar, FiCheckCircle } from 'react-icons/fi';
import HeroBanner from '../components/ui/HeroBanner';
import ProductCard from '../components/ui/ProductCard';
import Spinner from '../components/ui/Spinner';
import API from '../api/axios';
import { getImageUrl } from '../utils/helpers';

const features = [
  { icon: FiTruck, title: 'Free Delivery', desc: 'On orders above PKR 5,000', color: 'text-blue-500 bg-blue-50' },
  { icon: FiShield, title: 'Quality Assured', desc: 'International medical standards', color: 'text-green-500 bg-green-50' },
  { icon: FiHeadphones, title: '24/7 Support', desc: 'Expert team always available', color: 'text-purple-500 bg-purple-50' },
  { icon: FiRefreshCw, title: 'Easy Returns', desc: '7-day hassle-free returns', color: 'text-orange-500 bg-orange-50' },
];

const catIcons = ['🏥', '⚡', '♿', '🏠', '🔪', '🦵', '🔬', '🪆'];

const HomePage = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [latestProducts, setLatestProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [middleBanners, setMiddleBanners] = useState([]);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [feat, cats, latest, banners] = await Promise.all([
          API.get('/products/featured'),
          API.get('/categories'),
          API.get('/products?limit=8&sort=latest'),
          API.get('/banners?position=middle'),
        ]);
        setFeaturedProducts(feat.data);
        setCategories(cats.data.slice(0, 8));
        setLatestProducts(latest.data.products);
        setMiddleBanners(banners.data);
      } catch {
        // fail silently
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  return (
    <div>
      {/* Hero Banner */}
      <HeroBanner />

      {/* Feature strip */}
      <div className="bg-white border-b border-gray-100 shadow-sm">
        <div className="container-custom">
          <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-gray-100">
            {features.map(({ icon: Icon, title, desc, color }) => (
              <div key={title} className="flex items-center gap-3 px-5 py-4 hover:bg-gray-50 transition-colors">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
                  <Icon size={18} />
                </div>
                <div>
                  <p className="font-semibold text-sm text-gray-800">{title}</p>
                  <p className="text-xs text-gray-500">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Categories */}
      <div className="container-custom py-14">
        <div className="flex justify-between items-end mb-8">
          <div>
            <p className="text-primary-500 text-xs font-bold uppercase tracking-widest mb-1">Browse</p>
            <h2 className="section-title">Shop by Category</h2>
          </div>
          <Link to="/shop" className="hidden sm:flex items-center gap-1.5 text-primary-500 hover:text-primary-700 text-sm font-semibold transition-colors group">
            View All <FiArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {loading
            ? [...Array(8)].map((_, i) => <div key={i} className="h-32 bg-gray-100 rounded-2xl animate-pulse" />)
            : categories.map((cat, idx) => (
                <Link
                  key={cat._id}
                  to={`/shop?category=${cat._id}`}
                  className="group relative card rounded-2xl p-5 flex flex-col items-center text-center border-2 border-transparent hover:border-primary-100 hover:shadow-md transition-all duration-200"
                >
                  <div className="w-14 h-14 bg-primary-50 group-hover:bg-primary-100 rounded-2xl flex items-center justify-center mb-3 transition-colors text-2xl">
                    {cat.image
                      ? <img src={getImageUrl(cat.image)} alt={cat.name} className="w-10 h-10 object-contain" />
                      : catIcons[idx] || '🏥'}
                  </div>
                  <span className="text-sm font-semibold text-gray-700 group-hover:text-primary-600 transition-colors leading-tight">{cat.name}</span>
                  <div className="absolute inset-x-0 bottom-0 h-0.5 bg-gradient-to-r from-primary-400 to-accent-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 rounded-full" />
                </Link>
              ))}
        </div>
      </div>

      {/* Featured Products */}
      <div className="bg-gradient-to-b from-gray-50 to-white py-14">
        <div className="container-custom">
          <div className="flex justify-between items-end mb-8">
            <div>
              <p className="text-yellow-500 text-xs font-bold uppercase tracking-widest mb-1 flex items-center gap-1">
                <FiStar size={11} className="fill-yellow-400" /> Featured
              </p>
              <h2 className="section-title">Top Products</h2>
            </div>
            <Link to="/shop?featured=true" className="hidden sm:flex items-center gap-1.5 text-primary-500 hover:text-primary-700 text-sm font-semibold transition-colors group">
              View All <FiArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
          {loading ? <Spinner /> : featuredProducts.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
              {featuredProducts.map((p) => <ProductCard key={p._id} product={p} />)}
            </div>
          ) : (
            <div className="text-center py-16 text-gray-400">No featured products yet.</div>
          )}
        </div>
      </div>

      {/* Promo Banners */}
      {middleBanners.length > 0 && (
        <div className="container-custom py-8">
          <div className={`grid gap-4 ${middleBanners.length === 1 ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'}`}>
            {middleBanners.slice(0, 2).map((banner) => (
              <Link
                key={banner._id}
                to={banner.link || '/shop'}
                className="relative rounded-2xl overflow-hidden flex items-center p-8 group"
                style={{ backgroundColor: banner.backgroundColor || '#1d6fa4', minHeight: '200px' }}
              >
                {banner.image && (
                  <img src={getImageUrl(banner.image)} alt={banner.title} className="absolute inset-0 w-full h-full object-cover opacity-20 group-hover:opacity-30 transition-opacity" />
                )}
                <div className="absolute inset-0 bg-gradient-to-r from-black/20 to-transparent" />
                <div className="relative z-10">
                  <h3 className="text-xl md:text-2xl font-bold mb-2" style={{ color: banner.textColor || '#fff' }}>{banner.title}</h3>
                  {banner.subtitle && <p className="text-sm mb-4 opacity-80" style={{ color: banner.textColor || '#fff' }}>{banner.subtitle}</p>}
                  <span className="inline-flex items-center gap-2 bg-white text-primary-700 font-bold px-5 py-2.5 rounded-xl text-sm shadow-md group-hover:shadow-lg transition-shadow">
                    {banner.buttonText || 'Shop Now'} <FiArrowRight size={14} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Latest Products */}
      <div className="container-custom py-14">
        <div className="flex justify-between items-end mb-8">
          <div>
            <p className="text-primary-500 text-xs font-bold uppercase tracking-widest mb-1">New Arrivals</p>
            <h2 className="section-title">Latest Products</h2>
          </div>
          <Link to="/shop?sort=latest" className="hidden sm:flex items-center gap-1.5 text-primary-500 hover:text-primary-700 text-sm font-semibold transition-colors group">
            View All <FiArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
        {loading ? <Spinner /> : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
            {latestProducts.map((p) => <ProductCard key={p._id} product={p} />)}
          </div>
        )}
      </div>

      {/* Why Choose Us */}
      <div className="bg-gradient-to-br from-primary-50 to-blue-50 py-16">
        <div className="container-custom">
          <div className="text-center mb-10">
            <p className="text-primary-500 text-xs font-bold uppercase tracking-widest mb-2">Why Hospitex</p>
            <h2 className="section-title">Pakistan's Most Trusted Medical Supplier</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: '🏆', title: '10+ Years Experience', desc: 'Trusted by thousands of hospitals and clinics across Pakistan.' },
              { icon: '📦', title: '500+ Products', desc: 'Comprehensive range from leading international medical brands.' },
              { icon: '🚚', title: 'Nationwide Delivery', desc: 'Fast and reliable delivery to all major cities in Pakistan.' },
              { icon: '✅', title: 'Certified Quality', desc: 'All products comply with international medical device standards.' },
              { icon: '💬', title: 'Expert Support', desc: 'Our specialists help you choose exactly the right equipment.' },
              { icon: '💰', title: 'Best Prices', desc: 'Competitive pricing with bulk discounts for institutions.' },
            ].map(({ icon, title, desc }) => (
              <div key={title} className="bg-white rounded-2xl p-6 flex items-start gap-4 shadow-sm hover:shadow-md transition-shadow border border-white hover:border-primary-100">
                <span className="text-3xl flex-shrink-0">{icon}</span>
                <div>
                  <h3 className="font-bold text-gray-800 mb-1 text-sm">{title}</h3>
                  <p className="text-gray-500 text-xs leading-relaxed">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="relative bg-primary-700 overflow-hidden py-16">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-64 h-64 bg-white rounded-full -translate-x-32 -translate-y-32" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full translate-x-48 translate-y-48" />
        </div>
        <div className="relative container-custom text-center text-white">
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 text-sm font-medium mb-5">
            <FiCheckCircle size={14} className="text-green-400" /> Trusted by 500+ Healthcare Facilities
          </div>
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Need Bulk Orders or Custom Equipment?</h2>
          <p className="text-primary-200 mb-8 text-lg max-w-xl mx-auto">
            We supply hospitals, clinics, and healthcare institutions across Pakistan.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="inline-flex items-center justify-center gap-2 bg-white text-primary-700 font-bold px-8 py-3.5 rounded-xl hover:bg-gray-50 transition-colors text-base shadow-lg">
              Contact Us <FiArrowRight size={16} />
            </Link>
            <Link to="/shop" className="inline-flex items-center justify-center gap-2 border-2 border-white/40 hover:border-white text-white font-semibold px-8 py-3.5 rounded-xl transition-all text-base">
              Browse All Products
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
